import {
  AppBar,
  Box,
  Collapse,
  CssBaseline,
  Toolbar,
  useMediaQuery,
  useScrollTrigger,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import clsx from "clsx";
import type React from "react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Footer } from "../../../components";
import { DrawerMenu } from "../../../components/drawer-menu";
import type { RootState } from "../../../store";
import { eventDetailsImagePath } from "../../consts/imgUrls";
import { useInIframe } from "../../hooks";
import { FreeBar } from "../freebar";
import { Header } from "../NewHeader";
import { HeaderProvider } from "../NewHeader/HeaderProvider";
import { useStyles } from "./new-participant-layout.styles";

const footerOptions = [
  {
    section: "FAQ",
    path: "/participant/faq",
  },
  {
    section: "Glossary",
    path: "/participant/glossary",
  },
  {
    section: "Help",
    path: "/participant/help",
  },
];

type Props = {
  children: React.ReactNode;
};

export const NewParticipantLayout = ({ children }: Props) => {
  const theme = useTheme();
  const isInIframe = useInIframe();
  const location = useLocation<{ isEventDetailsPage: boolean }>();
  const [screen, setScreen] = useState(["default"]);
  const [scrollTop, setScrollTop] = useState(0);
  const classes = useStyles({ scrollTop });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentView, setCurrentView] = useState("");
  const [anchorDropdown, setAnchorDropdown] = useState(false);
  const isPremium = useSelector<RootState>((state) => state.profile.isPremium);
  const showBanner = isPremium === false;

  useEffect(() => {
    const onScroll = () => {
      setScrollTop(window.pageYOffset);
    };
    window.addEventListener("scroll", onScroll);

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const smallDisplay = useMediaQuery(theme.breakpoints.down("sm"));

  const triggerScroll = useScrollTrigger();

  const handleToggleDrawer = () => {
    setScreen(["default"]);
    setMobileOpen(!mobileOpen);
  };

  const handleCloseDrawer = () => setMobileOpen(false);

  const setDrawerScreen = (currentScreen) => {
    if (currentScreen.length > 1) {
      setScreen(currentScreen);
    } else {
      setScreen([...screen, ...currentScreen]);
    }
  };

  useEffect(() => {
    setCurrentView(location.pathname?.split("/")[2]);
  }, [location.pathname]);

  if (isInIframe) {
    return <Box paddingRight={2}>{children}</Box>;
  }

  return (
    <div className={classes.root}>
      <CssBaseline />

      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar className={classes.toolbar} disableGutters>
          <HeaderProvider>
            <Header
              isOpenMenu={mobileOpen}
              handleClickMenu={handleToggleDrawer}
              anchorDropdown={anchorDropdown}
              setAnchorDropdown={setAnchorDropdown}
            />

            <Collapse in={smallDisplay ? !triggerScroll : true} timeout={1000}>
              {showBanner && <FreeBar />}
            </Collapse>
          </HeaderProvider>
        </Toolbar>
      </AppBar>

      <DrawerMenu
        mobileOpen={mobileOpen}
        onClickMenuOption={handleCloseDrawer}
        screen={screen}
        setScreen={setDrawerScreen}
      />

      {location.state?.isEventDetailsPage && (
        <div className={classes.learningImageContainer}>
          <img
            className={classes.learningImage}
            src={eventDetailsImagePath}
            alt="Fruition"
          />
        </div>
      )}

      <main
        className={clsx(classes.content, {
          [classes.marginWithBanner]: showBanner,
        })}
      >
        <div className={classes.toolbar} />
        <div className={classes.childrenContainer}>{children}</div>
        <Footer>
          {footerOptions.map((option) => (
            <a
              key={option.section}
              href={option.path}
              className={clsx(classes.emailLink, {
                [classes.optionSelected]:
                  currentView?.toLowerCase() === option.section?.toLowerCase(),
              })}
            >
              {option.section}
            </a>
          ))}
        </Footer>
      </main>
    </div>
  );
};
